"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DATABASE_SHARD_VALUE_KEY_LIMIT = exports.DATABASE_SHARD_MONTH_SET_LIMIT = exports.DATABASE_SHARD_VALUE_SET_LIMIT = exports.DATABASE_SHARD_VALUE_COLUMN_TYPES = exports.DATABASE_SHARD_VALUE_KEY_PREFIX = void 0;
exports.databaseShardValueColumns = databaseShardValueColumns;
exports.isDatabaseShardValueKey = isDatabaseShardValueKey;
exports.databaseShardValueColumnIds = databaseShardValueColumnIds;
exports.summarizeDatabaseShardValueKeys = summarizeDatabaseShardValueKeys;
exports.addDatabaseShardValueKeys = addDatabaseShardValueKeys;
exports.capDatabaseShardValueKeys = capDatabaseShardValueKeys;
exports.databaseShardValueKeysUntrackingColumn = databaseShardValueKeysUntrackingColumn;
exports.untrackDatabaseShardManifestColumn = untrackDatabaseShardManifestColumn;
exports.readDatabaseShardValueSummary = readDatabaseShardValueSummary;
exports.remapDatabaseShardValueColumns = remapDatabaseShardValueColumns;
exports.databaseShardCanMatch = databaseShardCanMatch;
exports.databaseShardCanMatchFilter = databaseShardCanMatchFilter;
const database_column_lineage_1 = require("./database-column-lineage");
const database_large_data_policy_1 = require("./database-large-data-policy");
const database_search_1 = require("./database-search");
/**
 * Value keys of one row shard, stored as keys of its manifest entry. The keys
 * only grow: every writer adds the keys of the values it writes, so writers
 * that edit the same shard concurrently merge to the union and no written
 * value goes missing. A shard entry loses keys only when it is laid out anew
 * from its complete rows (merge, split).
 *
 * - `sv:c:<column>` the column was summarized from every row of the shard.
 * - `sv:v:<hash>:<column>` a cell of the column holds the hashed value. Date
 *   columns are summarized by month, every other column by value.
 * - `sv:m:<month>:<column>` a cell of the date column reads as a date in that
 *   month.
 * - `sv:x:<column>` / `sv:y:<column>` values / months of a summarized column
 *   are no longer tracked; the column is then read.
 *
 * A column without its `sv:c` key is not tracked and carries no marker. Its
 * written values are still added, up to one past the limit, so a concurrent
 * summary that completes the column holds them or passes the limit.
 *
 * A reader may skip a shard only for a column that is summarized and tracked.
 * Anything else reads the shard.
 */
exports.DATABASE_SHARD_VALUE_KEY_PREFIX = 'sv:';
/** Column types whose cells are worth summarizing: few distinct values or dates. */
exports.DATABASE_SHARD_VALUE_COLUMN_TYPES = new Set([
    'checkbox',
    'date',
    'select',
]);
/** Distinct value hashes / months tracked per column and shard. */
exports.DATABASE_SHARD_VALUE_SET_LIMIT = database_large_data_policy_1.DATABASE_LARGE_DATA_POLICY.shard.valueSetLimit;
exports.DATABASE_SHARD_MONTH_SET_LIMIT = database_large_data_policy_1.DATABASE_LARGE_DATA_POLICY.shard.monthSetLimit;
/** Value and month keys tracked per shard entry. */
exports.DATABASE_SHARD_VALUE_KEY_LIMIT = database_large_data_policy_1.DATABASE_LARGE_DATA_POLICY.shard.valueKeyLimit;
/**
 * The columns of a stored definition (`prop:columns`) as shard entries
 * summarize them, read defensively: peers may write anything into a column.
 */
function databaseShardValueColumns(columns) {
    const valueColumns = [];
    for (const value of columns) {
        if (!value || typeof value !== 'object')
            continue;
        const { id, type, data, cellKey, predecessors } = value;
        if (typeof id !== 'string' || !id || typeof type !== 'string')
            continue;
        valueColumns.push({
            id,
            type,
            data,
            ...(typeof cellKey === 'string' && cellKey ? { cellKey } : {}),
            predecessors: (0, database_column_lineage_1.normalizeDatabaseColumnPredecessors)(predecessors),
        });
    }
    return valueColumns;
}
/** The cell of `column` in a row of stored cells, as every reader reads it. */
function readStoredCell(column, cells) {
    return (0, database_column_lineage_1.readDatabaseCell)(column, cellKey => Object.prototype.hasOwnProperty.call(cells, cellKey)
        ? (0, database_column_lineage_1.storedDatabaseCellValue)(cells[cellKey])
        : undefined);
}
const MISSING_TOKEN = 'u';
function valueToken(value) {
    if (value === undefined)
        return MISSING_TOKEN;
    if (value === null)
        return 'null';
    switch (typeof value) {
        case 'string':
            return `s:${value}`;
        case 'boolean':
            return `b:${value}`;
        case 'number':
            // Object.is separates -0 from 0 and matches NaN with NaN.
            return Object.is(value, -0) ? 'n:-0' : `n:${value}`;
        default:
            return null;
    }
}
function hashToken(token) {
    let hash = 0x811c9dc5;
    for (let index = 0; index < token.length; index += 1) {
        hash ^= token.charCodeAt(index);
        hash = Math.imul(hash, 0x01000193);
    }
    return (hash >>> 0).toString(16).padStart(8, '0');
}
function monthOf(time) {
    const date = new Date(time);
    return date.getUTCFullYear() * 12 + date.getUTCMonth();
}
const completeKey = (columnId) => `sv:c:${columnId}`;
const valueKey = (hash, columnId) => `sv:v:${hash}:${columnId}`;
const monthKey = (month, columnId) => `sv:m:${month}:${columnId}`;
const valuesUntrackedKey = (columnId) => `sv:x:${columnId}`;
const monthsUntrackedKey = (columnId) => `sv:y:${columnId}`;
function isDatabaseShardValueKey(key) {
    return key.startsWith(exports.DATABASE_SHARD_VALUE_KEY_PREFIX);
}
/** Ids of the columns whose values shard entries summarize. */
function databaseShardValueColumnIds(columns) {
    return columns
        .filter(column => exports.DATABASE_SHARD_VALUE_COLUMN_TYPES.has(column.type))
        .map(column => column.id);
}
/** Date columns are summarized by month, every other column by value. */
const isDateColumn = (column) => column.type === 'date';
/** The value queries read from a stored cell: `{ columnId, value }` cells are
 * unwrapped, plain cells are read as they are. */
function queryValue(cell) {
    return cell &&
        typeof cell === 'object' &&
        !Array.isArray(cell) &&
        'columnId' in cell &&
        'value' in cell
        ? cell.value
        : cell;
}
const MISSING_HASH = hashToken(MISSING_TOKEN);
/** The tracked key a stored cell of `column` adds, or null for none: a cell
 * of a date column that reads as no date, or an object value, which never
 * equals a query value. */
function trackedKeyOf(column, cell) {
    const value = queryValue(cell);
    if (isDateColumn(column)) {
        const time = (0, database_search_1.parseDatabaseProjectionDate)(value);
        return time === undefined ? null : monthKey(monthOf(time), column.id);
    }
    const token = valueToken(value);
    return token === null ? null : valueKey(hashToken(token), column.id);
}
const isTrackedKey = (key) => key.startsWith('sv:v:') || key.startsWith('sv:m:');
const columnOfTracked = (key) => key.slice(key.indexOf(':', 5) + 1);
/** Tracked keys of one kind and column share a group. */
const groupOf = (key) => key.slice(0, 5) + columnOfTracked(key);
const limitOfGroup = (group) => group.startsWith('sv:v:')
    ? exports.DATABASE_SHARD_VALUE_SET_LIMIT
    : exports.DATABASE_SHARD_MONTH_SET_LIMIT;
const untrackedKeyOfGroup = (group) => group.startsWith('sv:v:')
    ? valuesUntrackedKey(group.slice(5))
    : monthsUntrackedKey(group.slice(5));
/**
 * Keys of a shard laid out from all its rows, given as stored cells keyed by
 * cell key. A column is summarized only when it is tracked: columns whose
 * values or months pass their limit, or that no longer fit the key budget,
 * are left out. Cheaper columns are tracked first.
 */
function summarizeDatabaseShardValueKeys(rows, columns) {
    const collected = columns
        .filter(column => exports.DATABASE_SHARD_VALUE_COLUMN_TYPES.has(column.type))
        .map(column => ({
        column,
        limit: isDateColumn(column)
            ? exports.DATABASE_SHARD_MONTH_SET_LIMIT
            : exports.DATABASE_SHARD_VALUE_SET_LIMIT,
        // Every column may hold missing cells: rows created next to a full
        // summary do not have to list the columns they leave empty.
        tracked: new Set(isDateColumn(column) ? [] : [valueKey(MISSING_HASH, column.id)]),
    }));
    for (const cells of rows)
        for (const entry of collected) {
            if (!entry.tracked)
                continue;
            const key = trackedKeyOf(entry.column, readStoredCell(entry.column, cells)?.value);
            if (key === null)
                continue;
            entry.tracked.add(key);
            if (entry.tracked.size > entry.limit)
                entry.tracked = null;
        }
    const keys = [];
    let budget = exports.DATABASE_SHARD_VALUE_KEY_LIMIT;
    const candidates = collected
        .flatMap(({ column, tracked }) => (tracked ? [{ column, tracked }] : []))
        .sort((left, right) => left.tracked.size - right.tracked.size);
    for (const { column, tracked } of candidates) {
        if (tracked.size > budget)
            break;
        budget -= tracked.size;
        keys.push(completeKey(column.id), ...tracked);
    }
    return keys.sort();
}
/**
 * Keys to add to a shard entry holding `existing` for rows written into it,
 * given as their stored cells after the write. Values are added whether or
 * not the column is summarized yet, so a concurrent full summary still
 * covers them. Missing cells need no key; every summary counts them.
 */
function addDatabaseShardValueKeys(existing, rows, columns) {
    const valueColumns = columns.filter(column => exports.DATABASE_SHARD_VALUE_COLUMN_TYPES.has(column.type));
    const written = new Set();
    for (const cells of rows)
        for (const column of valueColumns) {
            const read = readStoredCell(column, cells);
            if (!read)
                continue;
            const key = trackedKeyOf(column, read.value);
            if (key !== null && key !== valueKey(MISSING_HASH, column.id))
                written.add(key);
        }
    const stored = new Set(existing);
    return capDatabaseShardValueKeys(stored, written).filter(key => !stored.has(key));
}
/**
 * Complete keys of an entry holding `stored` after a writer added `next`.
 * Keys only grow, so additions are bounded against what is stored:
 *
 * - A summarized column whose values or months would pass their limit, or an
 *   entry whose tracked keys would pass the key limit, gets the untracked
 *   marker of that kind instead of further keys.
 * - A column `next` summarizes becomes summarized only when all its keys fit;
 *   otherwise it stays unsummarized.
 * - An unsummarized column gets its keys up to one past the limit and no
 *   marker: a concurrent summary of the column then either holds the written
 *   values or passes the limit, and reads as untracked.
 */
function capDatabaseShardValueKeys(stored, next) {
    const keys = new Set(stored);
    const counts = new Map();
    for (const key of keys)
        if (isTrackedKey(key))
            counts.set(groupOf(key), (counts.get(groupOf(key)) ?? 0) + 1);
    const isSummarized = (columnId) => keys.has(completeKey(columnId));
    let tracked = 0;
    for (const [group, count] of counts)
        if (isSummarized(group.slice(5)))
            tracked += count;
    const summarizing = new Set();
    const added = new Map();
    for (const key of next ?? []) {
        if (keys.has(key))
            continue;
        if (key.startsWith('sv:c:'))
            summarizing.add(key.slice(5));
        else if (isTrackedKey(key)) {
            const list = added.get(groupOf(key));
            if (list)
                list.push(key);
            else
                added.set(groupOf(key), [key]);
        }
        else
            keys.add(key);
    }
    const groupsOf = (columnId) => ['sv:v:' + columnId, 'sv:m:' + columnId].filter(group => added.has(group));
    const addAll = (group) => {
        const list = added.get(group);
        for (const key of list)
            keys.add(key);
        counts.set(group, (counts.get(group) ?? 0) + list.length);
    };
    const columnIds = new Set([
        ...summarizing,
        ...[...added.keys()].map(group => group.slice(5)),
    ]);
    for (const columnId of [...columnIds].sort()) {
        if (isSummarized(columnId)) {
            for (const group of groupsOf(columnId)) {
                const untracked = untrackedKeyOfGroup(group);
                if (keys.has(untracked))
                    continue;
                const size = added.get(group).length;
                if ((counts.get(group) ?? 0) + size > limitOfGroup(group) ||
                    tracked + size > exports.DATABASE_SHARD_VALUE_KEY_LIMIT) {
                    keys.add(untracked);
                    continue;
                }
                addAll(group);
                tracked += size;
            }
            continue;
        }
        const total = (group) => (counts.get(group) ?? 0) + (added.get(group)?.length ?? 0);
        const kinds = ['sv:v:' + columnId, 'sv:m:' + columnId];
        const size = kinds.reduce((sum, group) => sum + total(group), 0);
        if (summarizing.has(columnId) &&
            kinds.every(group => total(group) <= limitOfGroup(group)) &&
            tracked + size <= exports.DATABASE_SHARD_VALUE_KEY_LIMIT) {
            keys.add(completeKey(columnId));
            for (const group of groupsOf(columnId))
                addAll(group);
            tracked += size;
            continue;
        }
        for (const group of groupsOf(columnId)) {
            let count = counts.get(group) ?? 0;
            for (const key of added.get(group).sort()) {
                if (count > limitOfGroup(group))
                    break;
                keys.add(key);
                count += 1;
            }
            counts.set(group, count);
        }
    }
    return [...keys].sort();
}
/**
 * Keys an entry holding `stored` gains when the type of `columnId` changes
 * from `change.from` to `change.to`: both untracked markers, whether or not
 * the entry summarized the column. A writer that still knows the old type
 * adds keys of the old kind, which a layout under the new type does not
 * cover, and keys never shrink. So the column never rules the entry out until
 * a merge or split lays the shard out anew under the new type. A change
 * between two types no summary covers needs no marker.
 */
function databaseShardValueKeysUntrackingColumn(stored, columnId, change) {
    if (!exports.DATABASE_SHARD_VALUE_COLUMN_TYPES.has(change.from) &&
        !exports.DATABASE_SHARD_VALUE_COLUMN_TYPES.has(change.to))
        return [];
    const keys = new Set(stored);
    return [valuesUntrackedKey(columnId), monthsUntrackedKey(columnId)].filter(key => !keys.has(key));
}
/**
 * `manifest` after the type of `columnId` changed from `change.from` to
 * `change.to`: every shard entry gains the untracked markers of
 * {@link databaseShardValueKeysUntrackingColumn}, so no reader rules a shard
 * out by values of the old type.
 */
function untrackDatabaseShardManifestColumn(manifest, columnId, change) {
    return {
        ...manifest,
        shards: manifest.shards.map(shard => {
            const added = databaseShardValueKeysUntrackingColumn(shard.valueKeys, columnId, change);
            return added.length
                ? { ...shard, valueKeys: [...(shard.valueKeys ?? []), ...added].sort() }
                : shard;
        }),
    };
}
/** What readers may conclude from the stored keys of one shard entry. */
function readDatabaseShardValueSummary(keys) {
    if (!keys)
        return undefined;
    const complete = new Set();
    const untrackedValues = new Set();
    const untrackedMonths = new Set();
    const hashes = new Map();
    const months = new Map();
    for (const key of keys) {
        const kind = key.slice(0, 5);
        if (kind === 'sv:c:')
            complete.add(key.slice(5));
        else if (kind === 'sv:x:')
            untrackedValues.add(key.slice(5));
        else if (kind === 'sv:y:')
            untrackedMonths.add(key.slice(5));
        else if (kind === 'sv:v:' || kind === 'sv:m:') {
            const split = key.indexOf(':', 5);
            const columnId = key.slice(split + 1);
            const token = key.slice(5, split);
            if (kind === 'sv:v:')
                hashes.set(columnId, [...(hashes.get(columnId) ?? []), token]);
            else
                months.set(columnId, [...(months.get(columnId) ?? []), Number(token)]);
        }
    }
    if (!complete.size)
        return undefined;
    const columns = {};
    for (const columnId of complete) {
        const values = hashes.get(columnId) ?? [];
        // Summarized values always count missing cells; a summarized column
        // without that value is a date column, summarized by month.
        if (values.includes(MISSING_HASH)) {
            columns[columnId] =
                untrackedValues.has(columnId) ||
                    values.length > exports.DATABASE_SHARD_VALUE_SET_LIMIT
                    ? {}
                    : { v: values.sort() };
            continue;
        }
        const dates = months.get(columnId) ?? [];
        columns[columnId] =
            untrackedMonths.has(columnId) ||
                dates.length > exports.DATABASE_SHARD_MONTH_SET_LIMIT
                ? {}
                : { m: dates.sort((left, right) => left - right) };
    }
    return { columns };
}
function hasHash(column, value) {
    const token = valueToken(value);
    if (!column?.v || token === null)
        return true;
    return column.v.includes(hashToken(token));
}
function canMatch(predicate, columns) {
    switch (predicate.type) {
        case 'column': {
            if (predicate.operator !== 'equals')
                return true;
            const column = columns[predicate.propertyId];
            if (column?.m) {
                // An equal cell reads as the same date, so its month is summarized.
                const time = (0, database_search_1.parseDatabaseProjectionDate)(predicate.value);
                return time === undefined || column.m.includes(monthOf(time));
            }
            return hasHash(column, predicate.value);
        }
        case 'predicate-group':
            return predicate.operator === 'or'
                ? predicate.predicates.some(child => canMatch(child, columns))
                : predicate.predicates.every(child => canMatch(child, columns));
        case 'temporal-overlap': {
            const windowStart = (0, database_search_1.parseDatabaseProjectionDate)(predicate.windowStart);
            const windowEnd = (0, database_search_1.parseDatabaseProjectionDate)(predicate.windowEnd);
            // Invalid windows match no row but report a diagnostic per row read.
            if (windowStart === undefined ||
                windowEnd === undefined ||
                windowStart >= windowEnd)
                return true;
            const start = columns[predicate.startPropertyId]?.m;
            if (!start)
                return true;
            if (!start.length)
                return false;
            let latestEnd = start.at(-1);
            if (predicate.endPropertyId) {
                const end = columns[predicate.endPropertyId]?.m;
                if (!end)
                    return true;
                if (end.length)
                    latestEnd = Math.max(latestEnd, end.at(-1));
            }
            return (start[0] <= monthOf(windowEnd) && latestEnd >= monthOf(windowStart));
        }
        case 'text-search':
            return true;
    }
}
/**
 * The summary under the column ids a reader presents. A column mapped to null
 * is left out, so a predicate on it reads the shard.
 */
function remapDatabaseShardValueColumns(columns, toColumnId) {
    if (!columns)
        return undefined;
    const mapped = {};
    for (const [columnId, column] of Object.entries(columns)) {
        const id = toColumnId(columnId);
        if (id !== null)
            mapped[id] = column;
    }
    return mapped;
}
/** False only when no row described by the summary can match every predicate. */
function databaseShardCanMatch(columns, predicates) {
    return predicates.every(predicate => canMatch(predicate, columns));
}
/** Every string a select cell may hold for `expected` to match it: the value,
 * its label and each option id carrying that label. */
function selectCandidates(expected, column) {
    const options = Array.isArray(column?.data?.options)
        ? column.data.options
        : [];
    const raw = String(expected);
    const label = options.find(option => option.id === raw)?.value ?? raw;
    const candidates = new Set([raw, String(label)]);
    for (const option of options)
        if (option.value === label && typeof option.id === 'string')
            candidates.add(option.id);
    return [...candidates].flatMap(candidate => {
        const number = Number(candidate);
        return candidate.trim() && Number.isFinite(number)
            ? [candidate, number]
            : [candidate];
    });
}
const FALSY_HASHES = new Set([false, undefined, null, 0, ''].map(value => hashToken(valueToken(value))));
function canMatchFilter(filter, columns, schema) {
    if (filter.type === 'group') {
        if (!filter.conditions.length)
            return true;
        return filter.op === 'and'
            ? filter.conditions.every(child => canMatchFilter(child, columns, schema))
            : filter.conditions.some(child => canMatchFilter(child, columns, schema));
    }
    const columnId = filter.left?.name;
    if (!columnId)
        return true;
    const summary = columns[columnId];
    const column = schema.get(columnId);
    const target = filter.args[0]?.value;
    switch (filter.function) {
        case 'isOneOf': {
            if (column?.type !== 'select' || !Array.isArray(target))
                return true;
            if (!target.length)
                return true;
            return target.some(expected => selectCandidates(expected, column).some(value => hasHash(summary, value)));
        }
        case 'isTrue':
        case 'isChecked':
            if (column?.type !== 'checkbox' || !summary?.v)
                return true;
            return summary.v.some(hash => !FALSY_HASHES.has(hash));
        case 'before':
        case 'after': {
            const time = (0, database_search_1.parseDatabaseProjectionDate)(target);
            if (column?.type !== 'date' || !summary?.m || time === undefined)
                return true;
            if (!summary.m.length)
                return false;
            return filter.function === 'before'
                ? summary.m[0] <= monthOf(time)
                : summary.m.at(-1) >= monthOf(time);
        }
        default:
            return true;
    }
}
/** False only when no row described by the summary can pass the view filter. */
function databaseShardCanMatchFilter(columns, filter, schema) {
    return canMatchFilter(filter, columns, new Map(schema.map(column => [column.id, column])));
}
